import React, { useState, useEffect } from 'react';
import { Input, Image, Modal, Upload, Button, Space, Tabs, Empty, Toast, Radio, InputNumber, Typography, Tag, Progress } from '@douyinfe/semi-ui';
import ColorPicker from '../../components/ColorPicker';
import UniImage from '../../components/UniImage';
import { IconImage, IconUpload, IconLink, IconHistory, IconApps } from '@douyinfe/semi-icons';
import { Icon } from 'tdesign-icons-react';
import { manifest } from './manifest';
import CryptoJS from 'crypto-js';
import styles from './index.module.scss';
import request from '../../utils/request';
import * as qiniu from 'qiniu-js';

// 将kebab-case转换为PascalCase格式的函数
const formatIconName = (name) => {
    return name.replace(/-(filled|stroke)$/, '');
};

const ImageSelector = ({ value, onChange, onClose, width = '100%', previewSize = 100, visible = false, previewBorderRadius = 4, enableTDesignIcon = false, showPreview = true }) => {
    const [imageUrl, setImageUrl] = useState(value || '');
    const [selectorVisible, setSelectorVisible] = useState(visible);
    const [activeTab, setActiveTab] = useState('url');
    const [localUploadLoading, setLocalUploadLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [historyImages, setHistoryImages] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPagination, setHistoryPagination] = useState({ currentPage: 1, pageSize: 20, total: 0 });
    const [historySearchKey, setHistorySearchKey] = useState('');
    const [tempUrl, setTempUrl] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);

    // TDesign图标相关状态
    const [iconStyle, setIconStyle] = useState('outline'); // 'filled' 或 'outline'
    const [selectedCategory, setSelectedCategory] = useState('');

    // 监听图标风格变化，重置分类选择
    useEffect(() => {
        const categories = Object.keys(manifest[iconStyle] || {});
        const firstCategory = categories[0] || '';
        setSelectedCategory('');  // 初始化为空字符串，默认选中'全部'
        setIconSearchKey('');
    }, [iconStyle]);
    const [selectedIcon, setSelectedIcon] = useState(null);
    const [iconColor, setIconColor] = useState('#000000');
    const [iconSize, setIconSize] = useState(24);
    const [iconSearchKey, setIconSearchKey] = useState('');

    useEffect(() => {
        setImageUrl(value || '');
    }, [value]);
    useEffect(() => {
        setSelectorVisible(visible);
        if (visible && activeTab === 'history') {
            fetchHistoryImages();
        }

        // 如果是图标格式，解析并设置相关状态
        if (value && value.startsWith('t-icon-')) {
            const params = value.replace('t-icon-', '').split(',');
            const [iconName, color = '#000000', size = '24'] = params;
            setSelectedIcon(iconName);
            setIconColor(color);
            setIconSize(parseInt(size));
            // 根据图标名称后缀判断风格
            if (iconName.endsWith('-filled')) {
                setIconStyle('filled');
            } else if (iconName.endsWith('-stroke')) {
                setIconStyle('outline');
            }
        }
    }, [visible]);

    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistoryImages();
        }
    }, [activeTab, historyPagination.currentPage, historySearchKey]);

    const fetchHistoryImages = async () => {
        setHistoryLoading(true);
        try {
            const response = await request.get('/go/sp/sp_file/list_sp_files/', {
                params: {
                    type: -1, // -1表示获取所有类型（图片和视频）
                    page: historyPagination.currentPage - 1,
                    limit: historyPagination.pageSize,
                    key: historySearchKey
                }
            });
            if (response.errno === 0) {
                setHistoryImages(response.list || []);
                setHistoryPagination(prev => ({
                    ...prev,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取历史图片失败');
            }
        } catch (error) {
            console.error('获取历史图片失败:', error);
            Toast.error('获取历史图片失败');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleChange = (url, fileType) => {
        setImageUrl(url);
        // 如果传入了文件类型，则更新selectedImage状态
        if (fileType) {
            setSelectedImage({ type: fileType, file_url: url });
        }
        if (onChange) {
            onChange(url);
        }
        setSelectorVisible(false);
    };

    // 处理图标选择
    const handleIconSelect = (iconName) => {
        setSelectedIcon(iconName);
    };

    // 确认选择图标
    const handleIconConfirm = () => {
        if (selectedIcon) {
            const iconString = `t-icon-${selectedIcon},${iconColor},${iconSize}`;
            console.log('confirm', iconString);
            handleChange(iconString);
        }
    };

    // 过滤图标
    const getFilteredIcons = () => {
        const styleIcons = manifest?.[iconStyle] || {};
        const firstCategory = Object.keys(styleIcons)[0] || '';
        const currentCategory = selectedCategory || firstCategory;

        //console.log('当前分类:', currentCategory, 'ffffff', iconStyle);
        if (!selectedCategory && !iconSearchKey) {
            // 显示所有分类的图标
            return Object.values(styleIcons).flatMap(category => category?.icons || []);
        }

        let filteredIcons = [];

        if (selectedCategory && styleIcons[selectedCategory]) {
            filteredIcons = styleIcons[selectedCategory].icons || [];
        }

        if (iconSearchKey) {
            const searchLower = iconSearchKey.toLowerCase();
            const allIcons = [];

            // 如果有选择分类，只在该分类中搜索
            if (selectedCategory && styleIcons[selectedCategory]) {
                return styleIcons[selectedCategory].icons.filter(icon =>
                    icon.name.toLowerCase().includes(searchLower) ||
                    (icon.keywords && icon.keywords.some(keyword =>
                        keyword.toLowerCase().includes(searchLower)
                    ))
                );
            }

            // 否则在所有分类中搜索
            Object.values(styleIcons).forEach(category => {
                if (category.icons) {
                    category.icons.forEach(icon => {
                        if (icon.name.toLowerCase().includes(searchLower) ||
                            (icon.keywords && icon.keywords.some(keyword =>
                                keyword.toLowerCase().includes(searchLower)
                            ))
                        ) {
                            allIcons.push(icon);
                        }
                    });
                }
            });

            return allIcons;
        }

        return filteredIcons;
    };

    const handleLocalUpload = async ({ file, onError, onSuccess }) => {
        console.log('上传文件:', file);
        file = file.fileInstance
        setUploadProgress(0);
        Toast.info('正在处理文件，请稍候...');
        try {
            // 文件有效性检查
            if (!file) {
                onError(new Error('请选择有效的图片文件'));
                return false;
            }

            // 检查文件类型
            if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                throw new Error('请上传图片或视频文件');
            }

            // 根据文件类型设置type值
            const fileType = file.type.startsWith('video/') ? 2 : 1;

            // 从环境变量获取最大文件大小限制，默认为10MB
            const maxSize = process.env.REACT_APP_MAX_UPLOAD_SIZE || 10 * 1024 * 1024;
            if (file.size > maxSize) {
                const maxSizeMB = Math.floor(maxSize / (1024 * 1024));
                throw new Error(`图片大小不能超过${maxSizeMB}MB`);
            }

            // 计算文件MD5，使用分片读取避免内存溢出
            const chunkSize = 2 * 1024 * 1024; // 2MB chunks
            const chunks = Math.ceil(file.size / chunkSize);
            const spark = CryptoJS.algo.MD5.create();

            for (let i = 0; i < chunks; i++) {
                const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
                const reader = new FileReader();
                const arrayBuffer = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(new Error('文件读取失败: ' + error.message));
                    reader.readAsArrayBuffer(chunk);
                });
                const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
                spark.update(wordArray);
            }

            const md5 = spark.finalize().toString();

            // 准备上传参数
            const prepareResult = await request.post('/go/sp/sp_file/prepare_upload_local/', {
                md5,
                tags: 0,
                type: fileType, // 1: 图片类型, 2: 视频类型
                file_size: file.size.toString(),
                file_name: file.name
            });

            if (prepareResult.errno !== 0) {
                throw new Error(prepareResult.errstr || '准备上传失败');
            }

            let fileUrl;
            if (prepareResult.file && prepareResult.file.file_url) {
                // 文件已存在，直接使用返回的URL
                fileUrl = prepareResult.file.file_url;
            } else {
                // 新文件，需要上传到本地服务器
                const formData = new FormData();
                formData.append('file', file);
                formData.append('upload_id', prepareResult.upload_id);
                formData.append('md5', md5);
                formData.append('tags', '0');
                formData.append('type', fileType.toString());
                formData.append('file_size', file.size.toString());
                formData.append('file_name', file.name);

                const uploadResult = await request.post('/go/sp/sp_file/upload_local/', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 6000000,  // 设置超时时间为6000秒
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                });

                if (uploadResult.errno !== 0) {
                    throw new Error(uploadResult.errstr || '上传失败');
                }

                fileUrl = uploadResult.file.file_url;
            }

            handleChange(fileUrl, fileType);

            return false; // 阻止默认上传行为
        } catch (error) {
            console.error('上传失败:', error);
            onError(error);
            Toast.error({ content: error.message || '上传失败', duration: 30000 });
            return false;
        }
    };

    const handleUpload = async ({ file, onError, onSuccess }) => {
        console.log('上传文件:', file);
        file = file.fileInstance
        Toast.info('正在处理文件，请稍候...');
        try {
            // 文件有效性检查
            if (!file) {
                onError(new Error('请选择有效的图片文件'));
                return false;
            }

            // 检查文件类型
            if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                throw new Error('请上传图片或视频文件');
            }

            // 根据文件类型设置type值
            const fileType = file.type.startsWith('video/') ? 2 : 1;

            // 从环境变量获取最大文件大小限制，默认为10MB
            const maxSize = process.env.REACT_APP_MAX_UPLOAD_SIZE || 10 * 1024 * 1024;
            if (file.size > maxSize) {
                const maxSizeMB = Math.floor(maxSize / (1024 * 1024));
                throw new Error(`图片大小不能超过${maxSizeMB}MB`);
            }

            // 计算文件MD5，使用分片读取避免内存溢出
            const chunkSize = 2 * 1024 * 1024; // 2MB chunks
            const chunks = Math.ceil(file.size / chunkSize);
            const spark = CryptoJS.algo.MD5.create();

            for (let i = 0; i < chunks; i++) {
                const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
                const reader = new FileReader();
                const arrayBuffer = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(new Error('文件读取失败: ' + error.message));
                    reader.readAsArrayBuffer(chunk);
                });
                const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
                spark.update(wordArray);
            }

            const md5 = spark.finalize().toString();

            // 准备上传参数
            const prepareResult = await request.post('/go/sp/sp_file/prepare_upload_qiniu/', {
                md5,
                tags: 0,
                type: fileType, // 1: 图片类型, 2: 视频类型
                file_size: file.size.toString(),
                file_name: file.name
            });

            if (prepareResult.errno !== 0) {
                throw new Error(prepareResult.errstr || '准备上传失败');
            }

            let fileUrl;
            if (prepareResult.file && prepareResult.file.file_url) {
                // 文件已存在，直接使用返回的URL
                fileUrl = prepareResult.file.file_url;
            } else {
                // 新文件，需要上传到七牛云
                const { qiniu_param, upload_id } = prepareResult;

                // 使用七牛云SDK上传，设置超时时间和配置
                const observable = qiniu.upload(file, qiniu_param.key, qiniu_param.token, {
                    config: {
                        useCdnDomain: true,
                        timeout: 6000000  // 设置超时时间为6000秒
                    }
                });
                const uploadResult = await new Promise((resolve, reject) => {
                    observable.subscribe({
                        next: (res) => {
                            // 显示上传进度
                            const percentCompleted = Math.round((res.total.loaded * 100) / res.total.size);
                            setUploadProgress(percentCompleted);
                        },
                        error: (err) => {
                            reject(new Error('上传七牛云失败: ' + err.message));
                        },
                        complete: (res) => {
                            resolve(res);
                        }
                    });
                });

                if (!uploadResult.key) {
                    throw new Error('上传七牛云失败');
                }

                // 构建文件URL
                fileUrl = `${qiniu_param.domain}${uploadResult.key}`;

                // 保存文件信息
                const saveResult = await request.post('/go/sp/sp_file/add_sp_files/', {
                    upload_id,
                    category_uid: '',
                    md5,
                    tags: 0,
                    type: fileType,
                    file_size: file.size.toString(),
                    file_name: file.name,
                    file_url: fileUrl
                });

                if (saveResult.errno !== 0) {
                    throw new Error(saveResult.errstr || '保存文件信息失败');
                }
            }

            console.log('上传成功:', fileUrl);
            handleChange(fileUrl, fileType);

            return false; // 阻止默认上传行为
        } catch (error) {
            console.error('上传失败:', error);
            onError(error);
            Toast.error(error.message || '上传失败');
            return false;
        }
    };

    const handleUrlConfirm = async () => {
        if (validateImage(tempUrl)) {
            try {
                // 从URL中提取文件名
                const fileName = tempUrl.split('/').pop() || 'image.jpg';
                let fileType = 1; // 默认为图片类型
                let saveParams = {
                    file_url: tempUrl,
                    category_uid: '',
                    type: fileType,
                    file_name: fileName
                };

                try {
                    // 尝试获取图片信息
                    const response = await fetch(tempUrl, { method: 'HEAD' });
                    const contentLength = response.headers.get('content-length');
                    const contentType = response.headers.get('content-type');

                    if (!contentType?.startsWith('image/') && !contentType?.startsWith('video/')) {
                        Toast.error('请输入有效的图片或视频地址');
                        return;
                    }

                    // 根据文件类型设置type值
                    fileType = contentType?.startsWith('video/') ? 2 : 1;
                    saveParams.type = fileType;

                    if (contentLength) {
                        saveParams.file_size = contentLength;
                    }
                } catch (error) {
                    console.warn('获取图片信息失败，继续保存图片:', error);
                }

                // 保存第三方图片信息
                const saveResult = await request.post('/go/sp/sp_file/add_third_sp_files/', saveParams);

                if (saveResult.errno !== 0) {
                    Toast.error(saveResult.errstr || '保存图片信息失败');
                    return;
                }
            } catch (error) {
                console.error('保存图片信息失败:', error);
                Toast.error('保存图片信息失败');
                return;
            }

            handleChange(tempUrl);
        }
    };

    const validateImage = (url) => {
        //console.log("valid what", url)
        return url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('t-icon') || url.startsWith('//'));
    };

    return (
        <div className={styles.imageSelector} style={{ width }}>
            {showPreview && <div className={styles.previewWrapper} onClick={() => setSelectorVisible(true)} style={{ cursor: 'pointer', width: previewSize, height: previewSize }}>
                {validateImage(imageUrl) ? (
                    imageUrl.startsWith('t-icon-') ? (
                        <UniImage
                            src={imageUrl}
                            style={{ objectFit: 'cover', borderRadius: previewBorderRadius, width: '100%', height: '100%' }}
                        />
                    ) : (
                        selectedImage?.type === 2 ? (
                            <video
                                src={imageUrl}
                                style={{ objectFit: 'cover', borderRadius: previewBorderRadius, width: '100%', height: '100%' }}
                                controls
                                playsInline
                            />
                        ) : (
                            <UniImage
                                src={imageUrl}
                                style={{ objectFit: 'cover', borderRadius: previewBorderRadius, width: '100%', height: '100%' }}
                            />
                        )
                    )
                ) : (
                    <div className={styles.emptyPreview}>
                        <IconImage size="large" />
                    </div>
                )}
            </div>}
            <Modal
                title="选择图片"
                maskClosable={false}
                visible={selectorVisible}
                onCancel={() => {
                    setSelectorVisible(false);
                    if (onClose) onClose();
                }}
                footer={null}
                width={700}
                centered
                className={styles.imageSelector}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabBarStyle={{ marginBottom: 24 }}
                >
                    <Tabs.TabPane
                        tab={<span>
                            <IconUpload />
                            本地上传
                        </span>}
                        itemKey="local"
                    >
                        <div className={styles.uploadArea}>
                            <Upload
                                accept="image/*,video/*"
                                showUploadList={false}
                                customRequest={handleLocalUpload}
                            >
                                <Button icon={<IconUpload />} loading={localUploadLoading}>选择图片</Button>
                            </Upload>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div style={{ marginTop: '10px', width: '100%' }}>
                                    <Progress percent={uploadProgress} showInfo={true} />
                                </div>
                            )}
                        </div>
                    </Tabs.TabPane>
                    {enableTDesignIcon && (
                        <Tabs.TabPane
                            tab={<span>
                                <IconApps />
                                图标
                            </span>}
                            itemKey="tdesign"
                        >
                            <div className={styles.iconSelector}>
                                <div className={styles.iconSelectorHeader}>
                                    <Radio.Group type="button" value={iconStyle} onChange={e => setIconStyle(e.target.value)}>
                                        <Radio value="outline">描边风格</Radio>
                                        <Radio value="filled">填充风格</Radio>
                                    </Radio.Group>

                                    <Input
                                        placeholder="搜索图标"
                                        value={iconSearchKey}
                                        onChange={setIconSearchKey}
                                        style={{ width: '100%', marginTop: 16, marginBottom: 16 }}
                                    />
                                </div>

                                <div className={styles.categoryLayout}>
                                    {!iconSearchKey && (
                                        <div className={styles.categoryPanel}>
                                            <Typography.Title heading={6} style={{ marginBottom: 8 }}>分类</Typography.Title>
                                            <Radio.Group
                                                value={selectedCategory}
                                                onChange={(e) => setSelectedCategory(e.target.value)}
                                                optionType="button"
                                                direction="vertical"
                                            >
                                                <Radio value="">全部</Radio>
                                                {Object.entries(manifest[iconStyle] || {}).map(([key, category]) => (
                                                    <Radio value={key} key={key}>
                                                        {category.labelCN}
                                                    </Radio>
                                                ))}
                                            </Radio.Group>
                                        </div>
                                    )}

                                    <div className={styles.iconMainArea}>
                                        <div className={styles.iconGrid}>
                                            {getFilteredIcons().map(icon => (
                                                <div
                                                    key={icon.name}
                                                    className={`${styles.iconItem} ${selectedIcon === icon.name ? styles.selected : ''}`}
                                                    onClick={() => handleIconSelect(icon.name)}
                                                >
                                                    <div className={styles.iconPreview}>
                                                        <Icon
                                                            name={formatIconName(icon.name.replace(/-(filled|stroke)$/, ''))}
                                                            size={24}
                                                            color={selectedIcon === icon.name ? iconColor : '#000000'}
                                                        />
                                                    </div>
                                                    <div className={styles.iconName}>{icon.name.replace(/-(filled|stroke)$/, '')}</div>
                                                </div>
                                            ))}
                                        </div>

                                        {selectedIcon && (
                                            <div className={styles.iconSettings}>
                                                <div className={styles.iconControls}>
                                                    <div className={styles.selectedIconPreview}>
                                                        <Icon
                                                            name={formatIconName(selectedIcon.replace(/-(filled|stroke)$/, ''))}
                                                            size={iconSize}
                                                            color={iconColor}
                                                        />
                                                    </div>

                                                    <div className={styles.controlItem}>
                                                        <Typography.Text>颜色:</Typography.Text>
                                                        <ColorPicker value={iconColor} onChange={(color) => setIconColor(color)} />
                                                    </div>

                                                    <div className={styles.controlItem}>
                                                        <Typography.Text>大小:</Typography.Text>
                                                        <InputNumber
                                                            value={iconSize}
                                                            onChange={value => setIconSize(value)}
                                                            min={12}
                                                            max={64}
                                                        />
                                                    </div>
                                                </div>

                                                <Button
                                                    type="primary"
                                                    onClick={handleIconConfirm}
                                                >
                                                    确认选择
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Tabs.TabPane>
                    )}
                    <Tabs.TabPane
                        tab={<span><IconHistory style={{ marginRight: 8 }} />历史图片</span>}
                        itemKey="history"
                    >
                        <div className={styles.historySearch}>
                            <Input
                                value={historySearchKey}
                                onChange={setHistorySearchKey}
                                placeholder="搜索图片名称"
                                style={{ width: '100%', marginBottom: 16 }}
                            />
                        </div>
                        {historyLoading ? (
                            <div className={styles.historyLoading}>
                                <Empty description="加载中..." />
                            </div>
                        ) : historyImages.length > 0 ? (
                            <div className={styles.historyList}>
                                {historyImages.map((image) => {
                                    const isVideo = image.type === 2;
                                    return (
                                        <div
                                            key={image.uid}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedImage(image);
                                            }}
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                handleChange(image.file_url, image.type);
                                            }}
                                            className={`${styles.historyItem} ${selectedImage?.uid === image.uid ? styles.selected : ''}`}
                                        >
                                            <div style={{ width: '100%', height: '100px', overflow: 'hidden', borderRadius: '2px', position: 'relative' }}>
                                                {isVideo ? (
                                                    <>
                                                        <video
                                                            preload={'none'}
                                                            src={image.file_url}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            controls
                                                            playsInline
                                                        />
                                                        <div className={styles.videoTag}>视频</div>
                                                    </>
                                                ) : (
                                                    <UniImage
                                                        src={image.file_url}
                                                        width="100%"
                                                        height="100%"
                                                        alt={image.file_name}
                                                        style={{ objectFit: 'cover' }}
                                                    />
                                                )}
                                            </div>
                                            <div className={styles.historyItemInfo}>
                                                <div className={styles.fileName}>{image.file_name}</div>
                                                <div className={styles.fileTime}>{new Date(parseInt(image.create_time)).toLocaleString()}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <Empty description="暂无历史图片" />
                        )}
                        {historyImages.length > 0 && (
                            <div className={styles.historyActions}>
                                <Button
                                    type="primary"
                                    disabled={!selectedImage}
                                    onClick={() => {
                                        if (selectedImage) {
                                            handleChange(selectedImage.file_url, selectedImage.type);
                                        }
                                    }}
                                    style={{ marginBottom: 16 }}
                                >
                                    确认选择
                                </Button>
                            </div>
                        )}
                        {historyImages.length > 0 && (
                            <div className={styles.historyPagination}>
                                <Button
                                    disabled={historyPagination.currentPage === 1}
                                    onClick={() => setHistoryPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                                >
                                    上一页
                                </Button>
                                <span>第 {historyPagination.currentPage} 页</span>
                                <Button
                                    disabled={historyPagination.currentPage * historyPagination.pageSize >= historyPagination.total}
                                    onClick={() => setHistoryPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                                >
                                    下一页
                                </Button>
                            </div>
                        )}
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={<span><IconLink style={{ marginRight: 8 }} />图片地址</span>}
                        itemKey="url"
                    >
                        <div className={styles.urlInput}>
                            <Input
                                value={tempUrl}
                                onChange={setTempUrl}
                                placeholder="请输入图片URL地址"
                                style={{ marginBottom: 16 }}
                            />
                            {validateImage(tempUrl) && (
                                <div className={styles.preview}>
                                    {tempUrl.endsWith('.mp4') || tempUrl.endsWith('.webm') || tempUrl.endsWith('.ogg') ? (
                                        <video
                                            src={tempUrl}
                                            width={200}
                                            controls
                                            style={{ objectFit: 'contain', maxWidth: '100%' }}
                                        />
                                    ) : (
                                        <Image
                                            src={tempUrl}
                                            width={200}
                                            alt="Preview"
                                            style={{ objectFit: 'contain', maxWidth: '100%' }}
                                            fallback={<div className={styles.fallback}>加载失败</div>}
                                            preview={false}
                                        />
                                    )}
                                </div>
                            )}
                            <div style={{ marginBottom: 16, padding: 16, background: 'var(--semi-color-fill-0)', borderRadius: 4 }}>
                                <p style={{ marginBottom: 8 }}>您可以使用第三方免费图片上传服务，参考 <a href="https://postimages.org/" target="_blank" rel="noopener noreferrer">https://postimages.org/</a> 或者 <a href="https://picui.cn/" target="_blank" rel="noopener noreferrer">https://picui.cn/</a> 等 </p>
                                <p style={{ color: 'var(--semi-color-text-2)', fontSize: '12px' }}>上传完图片后, 复制图片https链接地址, 粘贴到上方输入框即可</p>
                                <p style={{ color: 'var(--semi-color-text-2)', fontSize: '12px' }}>注意：使用第三方免费图片时，将不保证图片的安全性，稳定性和长期性</p>
                            </div>
                            <Button type="primary" onClick={handleUrlConfirm}>确定</Button>
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={<span><IconUpload style={{ marginRight: 8 }} />上传到七牛云</span>}
                        itemKey="upload"
                    >
                        {!JSON.parse(localStorage.getItem('configStatus') || '{}').qiniu_oss && (
                            <div style={{ marginBottom: 16, padding: 16, background: 'var(--semi-color-fill-0)', borderRadius: 4 }}>
                                <p style={{ marginBottom: 8 }}>使用此功能需要先配置七牛云OSS参数</p>
                                <Button type="warning" theme="solid" onClick={() => window.location.href = '/settings'}>
                                    前往设置
                                </Button>
                            </div>
                        )}
                        <Upload
                            customRequest={handleUpload}
                            accept="image/*,video/*"
                            draggable
                            className={styles.uploader}
                        >
                            <div className={styles.uploadHint}>
                                <IconUpload size="extra-large" />
                                <p>点击或拖拽图片到这里上传</p>
                            </div>
                        </Upload>
                    </Tabs.TabPane>
                </Tabs>
            </Modal>
        </div>
    );
};

export { ImageSelector };
export default ImageSelector;